import React, { useEffect, useState, memo } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Section } from '../components/Section';
import { LimeBandCTA } from '../components/LimeBandCTA';
export function Practice() {
  const [rotation, setRotation] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const rotationDegrees = scrollPosition * 0.2;
      setRotation(rotationDegrees);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const topics = ['Agile Leadership', 'Agile Mindset', 'Campaign Planning', 'Shared Accountability', 'Cultural Engineering', 'Customer Centricity', 'Iterative Planning', 'Leading Through Change', 'Storytelling for Business'];
  return <div className="flex flex-col min-h-screen w-full bg-white" data-id="element-376">
      <Navbar data-id="element-377" />
      <main className="flex-grow" data-id="element-378">
        {/* Hero Section */}
        <Section className="pt-32 md:pt-48" data-id="element-379">
          <div className="flex flex-col md:flex-row md:gap-16 lg:gap-32" data-id="element-380">
            <div className="mb-8 md:mb-0 md:w-1/2" data-id="element-381">
              <h1 className="font-poppins font-normal text-3xl md:text-5xl text-black leading-tight mb-6" data-id="element-382">
                Move people. Not just plans.
              </h1>
              <div className="space-y-4" data-id="element-383">
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-384">
                  Change doesn't happen in an all-hands or a town hall. It
                  happens in the hallway, in the standup, in the 1:1. It happens
                  where the work gets done. Most strategies miss that—they're
                  built in a vacuum, shipped in a deck, pitched in a meeting,
                  and expected to drive instant results.
                </p>
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-385">
                  But people don't show up and shift how they work just because
                  they're told it's changing. They shift when it's clear why a
                  new way beats the old way. And most trainings don't get there.
                  They're built to tell, not empower. To show, not involve. To
                  teach, not transform.
                </p>
              </div>
            </div>
            <div className="md:w-1/2 overflow-hidden rounded-lg aspect-[4/3]" data-id="element-386">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pVrUETbqURZZ2yvsyyPdiJ/Practice_Image_1.jpg" alt="Modern white facade with geometric oval-shaped cutouts and colorful accents" className="w-full h-full object-cover" data-id="element-387" />
            </div>
          </div>
        </Section>
        {/* POV Section */}
        <Section data-id="element-388">
          <div className="max-w-4xl mx-auto text-center" data-id="element-389">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black mb-6" data-id="element-390">
              Most trainings aren't built to drive change.
            </h2>
            <div className="space-y-4" data-id="element-391">
              <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-392">
                They're built to explain it. That's a problem when what the
                business needs isn't just comprehension. It's conviction. People
                don't need another model to memorize. They need a better way to
                make sense of what's being asked of them and the space to try it
                on.
              </p>
              <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-393">
                Change requires more than explanation. It needs the kind of
                experience that levels the playing field, creates space to
                practice, and helps people see how it connects to their
                day-to-day.
              </p>
            </div>
          </div>
        </Section>
        {/* Home Image 2 */}
        <Section className="py-8 md:py-12" data-id="element-394">
          <div className="max-w-[170px] mx-auto" data-id="element-395">
            <div className="w-full aspect-square overflow-hidden rounded-lg" data-id="element-396">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8JnCnwFgpu5hq3nksuUQDp/Home_Image_2.png" alt="Abstract geometric pattern" className="w-full h-full object-cover" style={{
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.1s linear'
            }} data-id="element-397" />
            </div>
          </div>
        </Section>
        {/* Solution Section */}
        <Section data-id="element-398">
          <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12" data-id="element-399">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black mb-6" data-id="element-400">
              Built to move people, not just train them.
            </h2>
            <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-401">
              That's why our experiences are designed to build belief, not just
              skills. To give teams the clarity, confidence, and conviction to
              evolve by:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-402">
            <div className="rounded-lg p-6 md:p-8" data-id="element-403">
              <div className="w-16 h-16 md:w-20 md:h-20 mb-6" data-id="element-404">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/cRPCKFQDCAGfzqeNvureat/Leveling_Icon.png" alt="" className="w-full h-full object-contain" data-id="element-405" />
              </div>
              <h3 className="font-poppins font-normal text-xl md:text-2xl text-black mb-4" data-id="element-406">
                Leveling the playing field
              </h3>
              <p className="font-inter text-base leading-relaxed text-black" data-id="element-407">
                Stripping away titles, functions, and lingo to create shared
                understanding from the start, so everyone can engage as equals.
              </p>
            </div>
            <div className="rounded-lg p-6 md:p-8" data-id="element-408">
              <div className="w-16 h-16 md:w-20 md:h-20 mb-6" data-id="element-409">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kPvR9BLX7Qs4oSunMws3KZ/Exercising_Icon.png" alt="" className="w-full h-full object-contain" data-id="element-410" />
              </div>
              <h3 className="font-poppins font-normal text-xl md:text-2xl text-black mb-4" data-id="element-411">
                Exercising the change
              </h3>
              <p className="font-inter text-base leading-relaxed text-black" data-id="element-412">
                Simulating real dynamics, giving people space to try new
                behaviors in low-stakes, high-impact ways.
              </p>
            </div>
            <div className="rounded-lg p-6 md:p-8" data-id="element-413">
              <div className="w-16 h-16 md:w-20 md:h-20 mb-6" data-id="element-414">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9XXw5ESzFARzeycoatPJuZ/Connecting_Icon.png" alt="" className="w-full h-full object-contain" data-id="element-415" />
              </div>
              <h3 className="font-poppins font-normal text-xl md:text-2xl text-black mb-4" data-id="element-416">
                Connecting it to the work
              </h3>
              <p className="font-inter text-base leading-relaxed text-black" data-id="element-417">
                Helping teams carry the shift back into the meetings, decisions,
                and moments where it actually counts.
              </p>
            </div>
          </div>
        </Section>
        {/* Option Section */}
        <Section data-id="element-418">
          <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12" data-id="element-419">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black mb-6" data-id="element-420">
              How we help teams move.
            </h2>
            <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-421">
              Each of our experiences is rooted in behavioral science, designed
              to support business momentum, and tailored to your context.
              They're practical, collaborative, and in service of a bigger
              ambition. We design and run sessions that shift how people think,
              lead, and work on a variety of topics.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-422">
            {topics.map((topic, index) => <div key={index} className="border border-lightgray rounded-lg p-6 flex items-center justify-center text-center" data-id="element-423">
                <span className="font-inter text-base text-black" data-id="element-424">{topic}</span>
              </div>)}
          </div>
        </Section>
        {/* Featured Section */}
        <Section bgColor="lightgray" data-id="element-425">
          <div className="mb-8 md:mb-12" data-id="element-426">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black" data-id="element-427">
              Featured Experiences
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-428">
            {/* Column 1 */}
            <div className="space-y-6" data-id="element-429">
              <div className="overflow-hidden rounded-lg aspect-[16/9]" data-id="element-430">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gi6ZrqzJTY3F4Br8FyUZEd/Agile_Leadership.jpg" alt="Agile Leadership experience" className="w-full h-full object-cover" data-id="element-431" />
              </div>
              <div className="rounded-lg p-6 md:p-8" data-id="element-432">
                <h3 className="font-poppins font-normal text-xl md:text-2xl text-black mb-4" data-id="element-433">
                  Agile Leadership
                </h3>
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-434">
                  Most leaders know agility is essential. Few understand just
                  how critical a role they play. This immersive experience puts
                  leaders in the shoes of agile teams, breaking down the core
                  behaviors, decisions, and mindset shifts that make it work, so
                  leaders walk away ready to adapt, not just explain.
                </p>
              </div>
            </div>
            {/* Column 2 */}
            <div className="space-y-6" data-id="element-435">
              <div className="overflow-hidden rounded-lg aspect-[16/9]" data-id="element-436">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/s9XyR7Kn4tRpEmRxVSBHm9/Storytelling_for_business_.jpg" alt="Storytelling for Business experience" className="w-full h-full object-cover" data-id="element-437" />
              </div>
              <div className="rounded-lg p-6 md:p-8" data-id="element-438">
                <h3 className="font-poppins font-normal text-xl md:text-2xl text-black mb-4" data-id="element-439">
                  Storytelling for Business
                </h3>
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-440">
                  The best ideas don't win. The best told ones do. Drawing
                  inspiration from the big screen to the bookshelf, this
                  interactive workshop breaks down the mechanics of memorable
                  storytelling, giving teams practical and impactful ways to
                  craft clear, persuasive messages that move minds.
                </p>
              </div>
            </div>
          </div>
        </Section>
        {/* Closing Section */}
        <Section data-id="element-441">
          <div className="max-w-4xl mx-auto text-center" data-id="element-442">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black mb-6" data-id="element-443">
              Change is a choice. Make it one people want to make.
            </h2>
            <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-444">
              When people connect to what's changing, and why, they get curious,
              engaged, and ready to act. Our experiences help you unlock that
              moment. And everything that comes after.
            </p>
          </div>
        </Section>
        {/* CTA Band */}
        <LimeBandCTA text="Ready to get started?" linkText="Let's Connect" href="/contact" data-id="element-445" />
      </main>
      <Footer data-id="element-446" />
    </div>;
}